import React, { useState, useEffect, useCallback } from "react";
import axios from '../../api/apiClient';
import { FEATURES } from "../../config/features";

import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../common/APICommon';
import { SetAuditTrail, SetAuditTrailBulk } from './../Reports/AuditTrailReportEmployeeSetup';
import {
  Input,
} from '../../common/Input';
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';
import { Select } from "../../common/Select";
// icon
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';

function AreaOfOperation({ user, isHideSideMenu }) {
  // Use the hook for message management
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  var headers = [
    "ID",
    ...(FEATURES.DISTANTWATER_MODULE ? ["Distant Water Code"] : []),
    ...(FEATURES.AREAOFOPERATION_CODE ? ["Code"] : []),
    "Description",
    "Status",
    "Actions"
  ]

  var accessors = [
    "id",
    ...(FEATURES.DISTANTWATER_MODULE ? ["distantWaterCode"] : []),
    ...(FEATURES.AREAOFOPERATION_CODE ? ["code"] : []),
    "description",
    "isActive",
    "actions"
  ]

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [distantWaterOptions, setDistantWaterOptions] = useState([]);

  // Form state
  const [form, setForm] = useState({
    code: "",
    description: "",
    distantWaterId: "",
    isActive: true,
  });

  const resetLocalVariables = () => {
    setForm({
      code: FEATURES.AREAOFOPERATION_CODE ? "" : undefined,
      description: "",
      distantWaterId: FEATURES.DISTANTWATER_MODULE ? "" : undefined,
      isActive: true,
    });
    setSelectedData(null);
  }

  // Retrieve
  const [filterData, setFilterData] = useState([]);
  // Add
  const [showAddForm, setShowAddForm] = useState(false);
  // Update
  const [showEditForm, setShowEditForm] = useState(false);

  const apiUrl = domain + "/api/AreaOfOperation";
  const distantWaterUrl = domain + "/api/DistantWater";

  const fetchDistantWaters = useCallback(async () => {
    if (!FEATURES.DISTANTWATER_MODULE) return;
    try {
      const response = await axios.get(distantWaterUrl + "/active", {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const activeOnly = response.data.filter(d => d.isActive !== false);
      setDistantWaterOptions(activeOnly.map(d => ({
        value: d.id,
        label: d.code,
      })));
    } catch (error) {
      console.error('Error fetching distant waters:', error);
    }
  }, [distantWaterUrl]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      setData(response.data);
      setFilterData(response.data);
      console.log(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchDistantWaters();
    fetchData();
  }, [fetchDistantWaters, fetchData]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();

    if (!value) {
      // If input is empty, reset to original list
      setData(filterData);
      return;
    }

    const res = filterData.filter((f) => {
      const match1 = f.description?.toString().toLowerCase().includes(lowercaseValue);
      const match2 = f.id?.toString().startsWith(lowercaseValue);
      const matchCode = FEATURES.AREAOFOPERATION_CODE
        ? f.code?.toString().toLowerCase().includes(lowercaseValue)
        : false;
      const matchDistantWater = FEATURES.DISTANTWATER_MODULE
        ? f.distantWaterDescription?.toString().toLowerCase().includes(lowercaseValue)
        : false;

      return match1 || match2 || matchCode || matchDistantWater;
    });

    setData(res);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prepare data for API
    const apiData = {
      ...(FEATURES.AREAOFOPERATION_CODE && { code: form.code }),
      description: form.description || "",
      ...(FEATURES.DISTANTWATER_MODULE && { distantWaterId: form.distantWaterId ? parseInt(form.distantWaterId) : null }),
      isActive: form.isActive !== undefined ? form.isActive : true,
    };

    console.log('Add Submitting data:', apiData);

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Audit trail logic
      await SetAuditTrail({
        setupArea: "Area of Operation",
        actionTaken: "insert",
        field: "Add",
        newValue: FEATURES.AREAOFOPERATION_CODE
          ? `${apiData.code} - ${apiData.description}`
          : apiData.description,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id
      });

      await fetchData();

      showGeneralMessage("Area of Operation created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      console.error('Error creating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to create area of operation";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();

    if (!selectedData?.id) {
      showGeneralMessage("No record selected for update", false);
      return;
    }

    const apiData = {
      ...(FEATURES.AREAOFOPERATION_CODE && { code: form.code }),
      description: form.description || "",
      ...(FEATURES.DISTANTWATER_MODULE && { distantWaterId: form.distantWaterId ? parseInt(form.distantWaterId) : null }),
      isActive: form.isActive !== undefined ? form.isActive : true,
    };

    console.log('Updating data:', apiData);

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Audit trail logic
      let auditInfo = [];

      if (
        FEATURES.AREAOFOPERATION_CODE &&
        apiData.code !== selectedData.code
      ) {
        auditInfo.push({
          setupArea: "Area of Operation",
          actionTaken: "update",
          field: "Code",
          newValue: apiData.code,
          oldValue: selectedData.code,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (apiData.description !== selectedData.description) {
        auditInfo.push({
          setupArea: "Area of Operation",
          actionTaken: "update",
          field: "Description",
          newValue: `${apiData.description}`,
          oldValue: `${selectedData.description}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (FEATURES.DISTANTWATER_MODULE && apiData.distantWaterId !== selectedData.distantWaterId) {
        const selectedDW = distantWaterOptions.find(opt => opt.value === apiData.distantWaterId);
        const oldDW = distantWaterOptions.find(opt => opt.value === selectedData.distantWaterId);
        auditInfo.push({
          setupArea: "Area of Operation",
          actionTaken: "update",
          field: "Distant Water",
          newValue: selectedDW ? selectedDW.label : "-",
          oldValue: oldDW ? oldDW.label : "-",
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (apiData.isActive !== selectedData.isActive) {
        auditInfo.push({
          setupArea: "Area of Operation",
          actionTaken: "update",
          field: "Is Active?",
          newValue: `${apiData.isActive ? "Active" : "Inactive"}`,
          oldValue: `${selectedData.isActive ? "Active" : "Inactive"}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (auditInfo.length > 0) {
        await SetAuditTrailBulk({ data: auditInfo });
      }

      setShowEditForm(false);
      await fetchData();

      showGeneralMessage("Area of Operation updated successfully!", true);
      closeEditHandleClick();
    } catch (error) {
      console.error('Error updating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to update area of operation";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);

    setForm({
      ...(FEATURES.AREAOFOPERATION_CODE && { code: data.code || "" }),
      description: data.description || "",
      ...(FEATURES.DISTANTWATER_MODULE && { distantWaterId: data.distantWaterId?.toString() || "" }),
      isActive: data.isActive !== undefined ? data.isActive : true,
    });
    openEditHandleClick();
  };

  const openAddHandleClick = () => {
    setShowAddForm(true);
  };

  const closeAddHandleClick = () => {
    setShowAddForm(false);
    resetLocalVariables();
  };

  const openEditHandleClick = () => {
    setShowEditForm(true);
  };

  const closeEditHandleClick = () => {
    setShowEditForm(false);
    resetLocalVariables();
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">Active</span>
    ) : (
      <span className="rounded-full bg-red-100 px-2 py-1 text-xs text-red-800">Inactive</span>
    );
  };

  const actions = [
    {
      name: "Edit",
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: editHandleClick,
      visible: true,
    },
  ];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Maintenance >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"Area of Operation"}</span>
              </div>
              <h3>Area of Operation</h3>
            </div>
            <div className='space-y-4 p-4'>
              <SearchBar handleFilter={handleFilter} />

              <Button icon={<AddIcon />}
                name={"Add Area of Operation"}
                className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                onClick={openAddHandleClick}
              />
              <CreateTable
                tableName={"Areas of Operation"}
                headers={headers}
                accessors={accessors}
                data={data}
                actions={actions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={data?.length}
                customRenderers={{
                  isActive: (value) => getStatusBadge(value)
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <AddEditForm
        isOpen={showAddForm}
        onClose={closeAddHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleSubmit}
        modalName={"Add Area of Operation"}
        mode="add"
        distantWaterOptions={distantWaterOptions}
      />
      <AddEditForm
        isOpen={showEditForm}
        onClose={closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleUpdate}
        modalName={"Update Area of Operation"}
        mode="edit"
        distantWaterOptions={distantWaterOptions}
      />

      {/* Use the reusable GeneralMessage component */}
      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />

      <LoadingSpinner loading={loadingRequest || loading} />

    </div>
  )
}

function AddEditForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  mode = "add",
  distantWaterOptions,
}) {
  const [errorState, setErrorState] = useState(false);

  const submit = (e) => {
    e.preventDefault();

    // Validation
    if ((FEATURES.AREAOFOPERATION_CODE && !form.code) || !form.description) {
      setErrorState(true);
      return;
    }

    handleSubmit(e);
    setErrorState(false);
  };

  const handleClose = () => {
    onClose();
    setErrorState(false);
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  const isEditMode = mode === "edit";

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] flex items-center justify-center overflow-auto bg-black/50 p-3 text-gray-700"
      >
        <div className="relative flex h-auto max-h-[90vh] w-full flex-col rounded-lg bg-gray-50 shadow-xl md:w-[90%] lg:w-[50%] xl:w-[40%]">

          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-2 shadow-sm">
            <h2 className="flex items-center space-x-2 text-xl font-semibold text-gray-800">
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>

            <button
              onClick={handleClose}
              className="border-none bg-transparent p-1 text-gray-400 transition-colors hover:text-gray-700"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-grow space-y-4 overflow-y-auto p-6">

            {/* Area of Operation Form */}
            <div className="rounded-lg border border-gray-300 bg-white px-4 shadow-sm">
              <div className="border-b border-gray-300 py-1">
                <h3 className="font-medium text-gray-700">Area of Operation Information</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 pb-3 pt-3">

                {FEATURES.AREAOFOPERATION_CODE &&
                  <Input
                    label="Code:"
                    value={form.code}
                    onChange={(e) => handleInputChange('code', e.target.value)}
                    placeholder="Enter area of operation code"
                    isRequired={true}
                    error={errorState && !form.code}
                    errorMessage="Please fill up this field"
                  />
                }
                <Input
                  label="Description:"
                  value={form.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter area of operation description"
                  isRequired={true}
                  error={errorState && !form.description}
                  errorMessage="Please fill up this field"
                />
                
                {FEATURES.DISTANTWATER_MODULE &&
                  <Select
                    label="Distant Water:"
                    value={form.distantWaterId}
                    onChange={(e) => handleInputChange('distantWaterId', e.target.value)}
                    options={distantWaterOptions}
                    placeholder="Select distant water"
                  />
                }
                
                <div className="flex items-center pt-2">
                  <input
                    type="checkbox"
                    checked={form.isActive !== undefined ? form.isActive : true}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-600">
                    Active
                  </label>
                </div>
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-2 rounded-b-lg border-t border-gray-200 bg-white p-5 shadow-sm">
            <button
              onClick={handleClose}
              className="cursor-pointer rounded border-none bg-gray-400 px-4 py-2 text-white transition-colors hover:bg-gray-500"
            >
              Cancel
            </button>

            <button
              onClick={submit}
              className="cursor-pointer rounded border-none bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
            >
              {isEditMode ? "Update" : "Save"}
            </button>
          </div>

        </div>
      </div>
    </>
  );
}

export default AreaOfOperation;